<template>
  <div class="daily-learning-tab" v-loading="loading">
    <!-- 统计卡片 -->
    <el-row :gutter="20" class="summary-row">
      <el-col :span="6">
        <div class="summary-card">
          <div class="summary-icon" style="background: #ecf5ff; color: #409eff">
            <i class="el-icon-calendar"></i>
          </div>
          <div class="summary-content">
            <div class="summary-value">{{ dailyStats.avgDaily || 0 }}</div>
            <div class="summary-label">日均学习单词</div>
          </div>
        </div>
      </el-col>
      <el-col :span="6">
        <div class="summary-card">
          <div class="summary-icon" style="background: #f0f9ff; color: #67c23a">
            <i class="el-icon-data-line"></i>
          </div>
          <div class="summary-content">
            <div class="summary-value">{{ dailyStats.continuousDays || 0 }}</div>
            <div class="summary-label">连续学习天数</div>
          </div>
        </div>
      </el-col>
      <el-col :span="6">
        <div class="summary-card">
          <div class="summary-icon" style="background: #fef0f0; color: #f56c6c">
            <i class="el-icon-time"></i>
          </div>
          <div class="summary-content">
            <div class="summary-value">{{ dailyStats.avgDuration || 0 }}</div>
            <div class="summary-label">日均学习时长(分)</div>
          </div>
        </div>
      </el-col>
      <el-col :span="6">
        <div class="summary-card">
          <div class="summary-icon" style="background: #fdf6ec; color: #e6a23c">
            <i class="el-icon-star-on"></i>
          </div>
          <div class="summary-content">
            <div class="summary-value">{{ dailyStats.completionRate || 0 }}%</div>
            <div class="summary-label">目标完成率</div>
          </div>
        </div>
      </el-col>
    </el-row>

    <!-- 每日学习趋势图 -->
    <el-card class="chart-card" shadow="never">
      <div slot="header" class="card-header">
        <span>每日学习趋势</span>
        <el-radio-group v-model="chartPeriod" size="small" @change="handlePeriodChange">
          <el-radio-button label="7">最近7天</el-radio-button>
          <el-radio-button label="30">最近30天</el-radio-button>
          <el-radio-button label="90">最近90天</el-radio-button>
        </el-radio-group>
      </div>
      <div ref="dailyChart" class="chart-container"></div>
    </el-card>

    <!-- 每日学习详情列表 -->
    <el-card class="detail-card" shadow="never">
      <div slot="header" class="card-header">
        <span>每日学习详情</span>
      </div>
      <el-table
        :data="dailyRecords"
        style="width: 100%"
        max-height="500"
      >
        <el-table-column prop="date" label="日期" width="120" />
        <el-table-column prop="newWords" label="新学单词" width="100" align="center" />
        <el-table-column prop="reviewWords" label="复习单词" width="100" align="center" />
        <el-table-column prop="totalWords" label="总计" width="80" align="center">
          <template slot-scope="scope">
            <span class="highlight-number">{{ scope.row.totalWords }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="duration" label="学习时长" width="100" align="center">
          <template slot-scope="scope">
            <span>{{ scope.row.duration }} 分钟</span>
          </template>
        </el-table-column>
        <el-table-column prop="targetCompletion" label="目标完成" width="120">
          <template slot-scope="scope">
            <el-progress
              :percentage="scope.row.targetCompletion"
              :color="completionColor(scope.row.targetCompletion)"
            />
          </template>
        </el-table-column>
        <el-table-column prop="accuracy" label="正确率" width="100" align="center">
          <template slot-scope="scope">
            <span>{{ scope.row.accuracy }}%</span>
          </template>
        </el-table-column>
        <el-table-column prop="notes" label="备注" min-width="150" show-overflow-tooltip />
      </el-table>
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { mapActions } from 'vuex';

export default {
  name: 'DailyLearningTab',
  props: {
    studentId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      chartPeriod: '30',
      dailyStats: {},
      dailyRecords: [],
      dailyChart: null,
    };
  },
  created() {
    this.loadDailyData();
  },
  mounted() {
    this.initDailyChart();
  },
  beforeDestroy() {
    if (this.dailyChart) {
      this.dailyChart.dispose();
    }
  },
  methods: {
    ...mapActions('student', ['fetchDailyStats', 'fetchDailyRecords']),

    /**
     * 加载每日数据
     */
    async loadDailyData() {
      this.loading = true;
      try {
        const params = {
          studentId: this.studentId,
          days: parseInt(this.chartPeriod),
        };
        this.dailyStats = await this.fetchDailyStats(params);
        this.dailyRecords = await this.fetchDailyRecords(params);
        this.updateDailyChart();
      } catch (error) {
        console.error('加载每日数据失败:', error);
        this.$message.error('加载每日数据失败');
      } finally {
        this.loading = false;
      }
    },

    /**
     * 周期改变
     */
    handlePeriodChange() {
      this.loadDailyData();
    },

    /**
     * 初始化图表
     */
    initDailyChart() {
      this.dailyChart = echarts.init(this.$refs.dailyChart);
      this.updateDailyChart();
    },

    /**
     * 更新图表
     */
    updateDailyChart() {
      if (!this.dailyChart) return;

      const dates = this.dailyRecords.map((item) => item.date);
      const newWords = this.dailyRecords.map((item) => item.newWords);
      const reviewWords = this.dailyRecords.map((item) => item.reviewWords);
      const duration = this.dailyRecords.map((item) => item.duration);

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
          },
        },
        legend: {
          data: ['新学单词', '复习单词', '学习时长'],
          bottom: 10,
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '60',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: dates,
        },
        yAxis: [
          {
            type: 'value',
            name: '单词数',
            position: 'left',
          },
          {
            type: 'value',
            name: '时长(分钟)',
            position: 'right',
          },
        ],
        series: [
          {
            name: '新学单词',
            type: 'line',
            data: newWords,
            smooth: true,
            itemStyle: { color: '#409eff' },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(64, 158, 255, 0.3)' },
                { offset: 1, color: 'rgba(64, 158, 255, 0.05)' },
              ]),
            },
          },
          {
            name: '复习单词',
            type: 'line',
            data: reviewWords,
            smooth: true,
            itemStyle: { color: '#67c23a' },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(103, 194, 58, 0.3)' },
                { offset: 1, color: 'rgba(103, 194, 58, 0.05)' },
              ]),
            },
          },
          {
            name: '学习时长',
            type: 'bar',
            yAxisIndex: 1,
            data: duration,
            itemStyle: { color: '#e6a23c' },
          },
        ],
      };

      this.dailyChart.setOption(option);
    },

    /**
     * 完成率颜色
     */
    completionColor(completion) {
      if (completion >= 100) return '#67c23a';
      if (completion >= 80) return '#e6a23c';
      return '#f56c6c';
    },
  },
};
</script>

<style scoped>
.daily-learning-tab {
  padding: 10px 0;
}

.summary-row {
  margin-bottom: 20px;
}

.summary-card {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.summary-icon {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 24px;
}

.summary-content {
  flex: 1;
}

.summary-value {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 5px;
}

.summary-label {
  font-size: 13px;
  color: #909399;
}

.chart-card,
.detail-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  color: #303133;
}

.chart-container {
  height: 400px;
  width: 100%;
}

.highlight-number {
  color: #409eff;
  font-weight: 600;
}

@media (max-width: 1200px) {
  .summary-row .el-col {
    margin-bottom: 10px;
  }
}
</style>
